import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { VoyagerSearchService } from '../Services/VoyagerSearchService';
import { logError, logDebug } from '../../../../utils/logger';
import { FALLBACK_DEMO_DATA } from '../constants/fallbackData';
import type { VoyagerDetailItem } from '../models/detail-item';

interface UseDetailItemOptions {
  itemId?: string;
  returnUrl?: string;
  voyagerBaseUrl?: string;
  voyagerConfigId?: string;
}

interface UseDetailItemResult {
  item: VoyagerDetailItem | null;
  isLoading: boolean;
}

export const useDetailItem = (
  data?: UseDetailItemOptions,
): UseDetailItemResult => {
  const ssrItem = useSelector(
    (state: any) => state.reduxAsyncConnect?.voyagerItemDetails || null,
  );
  const routerSearch = useSelector(
    (state: any) => state.router?.location?.search || '',
  );

  const [item, setItem] = useState<VoyagerDetailItem | null>(ssrItem);
  const [isLoading, setIsLoading] = useState(!ssrItem);

  // Configure VoyagerSearchService with block settings
  useEffect(() => {
    if (data?.voyagerBaseUrl || data?.voyagerConfigId) {
      VoyagerSearchService.configure({
        baseUrl: data.voyagerBaseUrl,
        configId: data.voyagerConfigId,
      });
    }
  }, [data?.voyagerBaseUrl, data?.voyagerConfigId]);

  // Get item ID from URL params or props.
  // Uses router state so the ID is available during SSR (window is undefined on the server).
  const getItemId = useCallback((): string | null => {
    if (data?.itemId) return data.itemId;
    const search =
      typeof window !== 'undefined' ? window.location.search : routerSearch;
    const urlParams = new URLSearchParams(search);
    return urlParams.get('id') || urlParams.get('itemId');
  }, [data?.itemId, routerSearch]);

  // Fetch item details from Voyager API.
  // Skips the client-side fetch when the SSR-prefetched item already matches the current ID.
  useEffect(() => {
    const fetchItemDetails = async () => {
      const itemId = getItemId();

      if (!itemId) {
        logDebug('No item ID provided, using fallback demo data');
        setItem(FALLBACK_DEMO_DATA);
        setIsLoading(false);
        return;
      }

      // SSR already fetched this item — no need to re-fetch on the client
      if (ssrItem && ssrItem.id === itemId) {
        return;
      }

      try {
        setIsLoading(true);
        const detailItem = await VoyagerSearchService.fetchItemDetails(itemId);
        if (detailItem) {
          setItem(detailItem);
        } else {
          logDebug('Item not found in API, using fallback demo data');
          setItem(FALLBACK_DEMO_DATA);
        }
      } catch (err) {
        logError('Failed to fetch item details, using fallback demo data', err);
        setItem(FALLBACK_DEMO_DATA);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItemDetails();
  }, [getItemId, ssrItem, data?.voyagerBaseUrl, data?.voyagerConfigId]);

  return { item, isLoading };
};
